import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import ProductCartCounter from '../components/ProductCartCounter';
import CombinedCheckoutModal from '../components/CombinedCheckoutModal';
import SalonHeaderActions from '../components/SalonHeaderActions';
import { useSkinSession } from '../context/SkinSessionContext';
import { getSessionIdFromUrl, getSessionId, pathWithSessionId } from '../utils/sessionManager';
import { goSalonHubForNewUser } from '../utils/salonHomeNav';
import { saveSkinFlowState, getSkinFlowState } from '../utils/skinFlowState';
import { getApiBaseUrl, SALON_API_PREFIX } from '../utils/apiConfig';
import { resolveSalonMediaUrl } from '../utils/mediaUrl';
import { sanitizeHtml, stripHtml } from '../utils/sanitizeHtml';
import { fetchWithTimeout } from '../utils/fetchWithTimeout';
import { fetchSkinAnalysisBundle } from '../utils/skinAnalysisBundle';
import { resolveProductHref, resolveSegmentationProductActions, isValidProductUrl } from '../utils/segmentation';
import { persistProductCart, persistTreatmentCart } from '../utils/cartPersistence';
import { buildRecommendableConditions, buildRecommendationPayloadScores } from '../utils/recommendationSeverity';
import { getFranchiseOrganizationId, getFranchiseId } from '../../shared/franchiseFlowAuth';
import {
  isPresentationRecommendationOverride,
  resolvePresentationGuestGenderForApi,
  resolvePresentationOrganizationId,
} from '../../presentation/presentationRecommendations';
import '../styles/PageBackground.css';
import './ProductRecommendationsPage.css';

const ProductRecommendationsPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { token } = useSkinSession();

  const sessionIdFromState = location.state?.sessionId || null;
  const sessionIdFromUrl = getSessionIdFromUrl(location);
  const sessionIdFromStorage = getSessionId();
  const sessionId = sessionIdFromState || sessionIdFromUrl || sessionIdFromStorage || null;

  const effectiveState = useMemo(() => {
    if (
      location.state &&
      (location.state.skinResults || location.state.routineData || location.state.recommendations || location.state.treatmentCartItems != null)
    ) {
      return location.state;
    }
    if (!sessionId) return null;
    return getSkinFlowState(sessionId);
  }, [location.state, sessionId]);

  const userDataFromState = effectiveState?.userData || location.state?.userData || null;
  const capturedImageFromState = effectiveState?.capturedImage || location.state?.capturedImage || null;
  const skinResultsFromState = effectiveState?.skinResults || location.state?.skinResults || null;
  const routineDataFromState = effectiveState?.routineData || location.state?.routineData || null;
  const aqiDataFromState = effectiveState?.aqiData ?? location.state?.aqiData ?? null;
  const cartItemsFromState = effectiveState?.cartItems ?? location.state?.cartItems ?? null;
  const treatmentCartItemsFromState = effectiveState?.treatmentCartItems ?? location.state?.treatmentCartItems ?? null;
  const updatedCartFromCheckout = location.state?.updatedCartFromCheckout || false;
  const [cartItems, setCartItems] = useState(cartItemsFromState || location.state?.cartItems || {}); // Store cart items: { productId: { product, quantity } });
  const [treatmentCartItems, setTreatmentCartItems] = useState(
    () =>
      treatmentCartItemsFromState && typeof treatmentCartItemsFromState === 'object'
        ? treatmentCartItemsFromState
        : {}
  );

  // Persist full state (including cartItems) so it survives refresh
  useEffect(() => {
    if (!sessionId) return;
    const current = getSkinFlowState(sessionId) || {};
    const fromLocation = location.state && typeof location.state === 'object' ? location.state : {};
    const merged = {
      ...current,
      ...fromLocation,
      sessionId,
      cartItems,
      treatmentCartItems,
    };
    saveSkinFlowState(sessionId, merged);
  }, [sessionId, cartItems, treatmentCartItems, location.state]);
  const [recommendations, setRecommendations] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('morning');
  const [uspPopup, setUspPopup] = useState({ open: false, text: '', heading: '', position: { top: 0, left: 0 } });
  const [isCheckoutModalOpen, setIsCheckoutModalOpen] = useState(false);

  // Product suggestion config from session app-data
  const [productSuggestionEnabled, setProductSuggestionEnabled] = useState(true);
  const [organizationId, setOrganizationId] = useState(null);
  const [productRecommendationSource, setProductRecommendationSource] = useState('database');
  const [productsPageUrl, setProductsPageUrl] = useState('');
  const [productsPageUrls, setProductsPageUrls] = useState([]);
  const [isConfigLoaded, setIsConfigLoaded] = useState(false);
  const [addToCartEnabled, setAddToCartEnabled] = useState(false);
  const [productLinkEnabled, setProductLinkEnabled] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const applyConfig = (data) => {
      if (cancelled || !data) return;
      if (isPresentationRecommendationOverride()) {
        setProductSuggestionEnabled(true);
        setProductRecommendationSource('database');
        setOrganizationId(resolvePresentationOrganizationId(data?.organization_id));
        setAddToCartEnabled(true);
        setProductLinkEnabled(true);
        setIsConfigLoaded(true);
        return;
      }
      const product = String(data?.product_suggestion || '').trim().toLowerCase();
      if (product === 'yes' || product === 'true' || data?.product_suggestion === true) {
        setProductSuggestionEnabled(true);
      } else if (product === 'no' || product === 'false' || data?.product_suggestion === false) {
        setProductSuggestionEnabled(false);
      } else {
        setProductSuggestionEnabled(true);
      }

      const recSource = data?.product_recommendation_source === 'products_page' ? 'products_page' : 'database';

      const orgId = data?.organization_id;
      setOrganizationId(
        orgId != null && orgId !== ''
          ? String(orgId)
          : (getFranchiseOrganizationId() || null),
      );
      setProductRecommendationSource(recSource);
      setProductsPageUrl(String(data?.products_page_url || '').trim());
      const urls = Array.isArray(data?.products_page_urls)
        ? data.products_page_urls.map((u) => String(u || '').trim()).filter(Boolean)
        : [];
      setProductsPageUrls(urls.length ? urls : (data?.products_page_url ? [String(data.products_page_url).trim()] : []));
      const actions = resolveSegmentationProductActions(data);
      setAddToCartEnabled(actions.addToCart);
      setProductLinkEnabled(actions.productLink);
      setIsConfigLoaded(true);
    };

    if (!token) {
      const base = getApiBaseUrl() || process.env.REACT_APP_API_URL || '';
      fetchWithTimeout(`${base}${SALON_API_PREFIX}/config/product-recommendation`, { timeoutMs: 20000 })
        .then((res) => {
          if (!res.ok) throw new Error('Product recommendation config unavailable');
          return res.json();
        })
        .then(applyConfig)
        .catch(() => {
          if (!cancelled) {
            setProductSuggestionEnabled(true);
            setProductRecommendationSource('database');
            setProductsPageUrl('');
            setProductsPageUrls([]);
            setIsConfigLoaded(true);
          }
        });
      return () => { cancelled = true; };
    }

    fetchSkinAnalysisBundle(token)
      .then((data) => {
        if (cancelled) return;
        applyConfig(data);
      })
      .catch(() => {
        if (!cancelled) {
          setProductSuggestionEnabled(true);
          setIsConfigLoaded(true);
        }
      });

    return () => { cancelled = true; };
  }, [token]);

  // Restore cart state from navigation state when coming back from checkout
  useEffect(() => {
    if (updatedCartFromCheckout && cartItemsFromState) {
      setCartItems(cartItemsFromState);
    }
  }, [cartItemsFromState, updatedCartFromCheckout]);

  const API_BASE_URL = getApiBaseUrl() || process.env.REACT_APP_API_URL || '';

  const resolveProductImageUrl = (url) => resolveSalonMediaUrl(url);

  const toNumberOrNull = (v) => {
    if (v == null || v === '') return null;
    const n = Number(v);
    return Number.isNaN(n) ? null : n;
  };

  const hasProductDiscount = (p) => {
    const d = toNumberOrNull(p?.discount);
    const net = toNumberOrNull(p?.amount);
    const sale = toNumberOrNull(p?.sale_price);
    if (net == null) return false;
    if (d != null && d > 0 && sale != null && sale > 0 && sale < net) return true;
    if (net != null && sale != null && sale > 0 && sale < net) return true;
    return false;
  };

  const hasProductNetPrice = (p) => toNumberOrNull(p?.amount) != null;
  const hasProductAmount = (p) => toNumberOrNull(p?.amount) != null;

  const formatDiscountBadge = (p) => {
    const d = Number(p?.discount);
    if (Number.isNaN(d) || d <= 0) return null;
    if (d > 0 && d <= 1) return `${Math.round(d * 100)}% OFF`;
    if (d > 1 && d <= 100) return `${Math.round(d)}% OFF`;
    return `₹${Math.round(d)} OFF`;
  };

  const persistRecommendations = useCallback((data) => {
    if (!sessionId || !data || typeof data !== 'object') return;
    const current = getSkinFlowState(sessionId) || {};
    const fromLocation = location.state && typeof location.state === 'object' ? location.state : {};
    saveSkinFlowState(sessionId, {
      ...current,
      ...fromLocation,
      sessionId,
      recommendations: data,
      cartItems,
      treatmentCartItems,
    });
  }, [sessionId, location.state, cartItems, treatmentCartItems]);

  useEffect(() => {
    if (!sessionId) return;
    const fromState = effectiveState?.recommendations ?? location.state?.recommendations ?? null;
    if (fromState && typeof fromState === 'object') {
      setRecommendations(fromState);
      setIsLoading(false);
      return;
    }
    try {
      const cached = getSkinFlowState(sessionId)?.recommendations;
      if (cached && typeof cached === 'object') {
        setRecommendations(cached);
        setIsLoading(false);
      }
    } catch (e) {
      // ignore cache read issues
    }
  }, [sessionId, effectiveState?.recommendations, location.state?.recommendations]);

  const handleAddToCart = (product, quantity, productId) => {
    // Update cart state with product ID as key, storing both product data and quantity
    setCartItems(prev => {
      if (quantity === 0) {
        // Remove item from cart if quantity is 0
        const newCart = { ...prev };
        delete newCart[productId];
        return newCart;
      }
      return {
        ...prev,
        [productId]: {
          product: product,
          quantity: quantity
        }
      };
    });
  };

  const handleCheckout = () => {
    setIsCheckoutModalOpen(true);
  };

  const handleCloseCheckoutModal = () => {
    setIsCheckoutModalOpen(false);
  };

  const handleCartUpdate = (updatedCartItems) => {
    setCartItems(updatedCartItems);
  };

  const handleTreatmentCartUpdate = (updatedTreatmentCartItems) => {
    setTreatmentCartItems(updatedTreatmentCartItems || {});
  };

  const getTotalTreatmentCartItems = () =>
    Object.values(treatmentCartItems).reduce((sum, item) => sum + (item.quantity || 0), 0);

  const getTotalCombinedCartItems = () => getTotalCartItems() + getTotalTreatmentCartItems();

  // Persist cart to backend whenever cart changes (Add to Cart, +/- quantity, or modal update)
  const saveCartToBackend = useCallback((items) => {
    if (!API_BASE_URL || !sessionId) return;
    persistProductCart(API_BASE_URL, sessionId, token, items).then((r) => {
      if (r && !r.ok) console.warn('Could not save cart to backend');
    });
  }, [API_BASE_URL, sessionId, token]);

  const saveTreatmentCartToBackend = useCallback((items) => {
    if (!API_BASE_URL || !sessionId) return;
    persistTreatmentCart(API_BASE_URL, sessionId, token, items).then((r) => {
      if (r && !r.ok) console.warn('Could not save treatment cart to backend');
    });
  }, [API_BASE_URL, sessionId, token]);

  useEffect(() => {
    saveCartToBackend(cartItems);
  }, [cartItems, saveCartToBackend]);

  useEffect(() => {
    saveTreatmentCartToBackend(treatmentCartItems);
  }, [treatmentCartItems, saveTreatmentCartToBackend]);

  const handleProductLinkClick = useCallback((product, productUrl, e) => {
    if (e) e.stopPropagation();
    const numericProductId = Number(product?.id ?? product?.product_id);
    const productId = Number.isFinite(numericProductId) ? numericProductId : null;
    const baseUrl = (API_BASE_URL || '').replace(/\/$/, '');
    if (baseUrl && sessionId && productId) {
      fetchWithTimeout(`${baseUrl}${SALON_API_PREFIX}/record-viewed-product`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          skin_id: sessionId,
          session_id: sessionId,
          product_id: productId,
          ...(token && { app_session_token: token }),
        }),
        timeoutMs: 10000,
      }).catch(() => {});
    }
    if (productUrl) {
      window.open(productUrl, '_blank', 'noopener,noreferrer');
    }
  }, [API_BASE_URL, sessionId, token]);

  const getTotalCartItems = () => {
    return Object.values(cartItems).reduce((sum, item) => sum + (item.quantity || 0), 0);
  };

  // Recommended products depend on suggest-product config and counts.
  useEffect(() => {
    if (!isConfigLoaded) return;
    if (!productSuggestionEnabled) {
      setIsLoading(false);
      setRecommendations(null);
      return;
    }
    if (recommendations) {
      setIsLoading(false);
      return;
    }

    const fetchRecommendations = async () => {
      if (!skinResultsFromState?.skinScore) {
        setError('Missing required data');
        setIsLoading(false);
        return;
      }

      try {
        // First, try to fetch stored products from database
        if (sessionId) {
          try {
            const storedParams = new URLSearchParams();
            if (token) storedParams.set('app_session_token', token);
            if (organizationId) storedParams.set('organization_id', organizationId);
            const franchiseId = getFranchiseId();
            if (franchiseId) storedParams.set('franchise_id', String(franchiseId));
            if (productRecommendationSource === 'products_page') storedParams.set('refresh_live', '1');
            const storedQuery = storedParams.toString() ? `?${storedParams.toString()}` : '';
            const storedResponse = await fetchWithTimeout(
              `${API_BASE_URL}${SALON_API_PREFIX}/get-stored-products/${sessionId}${storedQuery}`,
              { timeoutMs: 20000 }
            );
            if (storedResponse.ok) {
              const storedData = await storedResponse.json().catch(() => ({}));
              const morning = storedData.recommended_products?.morning || [];
              const evening = storedData.recommended_products?.evening || [];
              const hasStoredProducts = (Array.isArray(morning) && morning.length > 0) || (Array.isArray(evening) && evening.length > 0);
              // products_page: always re-fetch live catalog (stored IDs are analytics-only snapshots)
              if (
                storedData.success
                && storedData.recommended_products
                && hasStoredProducts
                && productRecommendationSource !== 'products_page'
              ) {
                setRecommendations(storedData);
                persistRecommendations(storedData);
                setIsLoading(false);
                return; // Successfully loaded from database
              }
              if (productRecommendationSource === 'products_page' && hasStoredProducts) {
                console.info(
                  '[ProductRecommendations] products_page: regenerating from live catalog',
                  { storedMorning: morning.length, storedEvening: evening.length }
                );
              }
            }
          } catch (storedErr) {
            // If fetching stored products fails, continue to regenerate
            console.log('Could not fetch stored products, regenerating...', storedErr);
          }
        }

        // Regenerate products only if stored products not found
        const skinScore = skinResultsFromState.skinScore;
        const detections = skinResultsFromState.detections || [];
        const conditionScores = buildRecommendationPayloadScores(skinScore, detections);
        const detectedFromDetections = detections
          .map((d) => d.class_name || d.className || d.name)
          .filter(Boolean);
        const detectedConditions = buildRecommendableConditions(
          detectedFromDetections,
          conditionScores,
          detections,
        );

        const userData = userDataFromState || {};
        const userSkinType = userData.skin_type != null && userData.skin_type !== '' ? userData.skin_type : null;
        const userAge = userData.age != null && userData.age !== '' ? Number(userData.age) : null;
        const resolvedOrgId = organizationId || getFranchiseOrganizationId() || null;
        const resolvedFranchiseId = getFranchiseId() || null;
        const genderForApi = resolvePresentationGuestGenderForApi(userData.gender);

        console.log('[ProductRecommendations] recommend-products payload:', {
          user_info: { skin_type: userSkinType, age: userAge, userData_keys: Object.keys(userData || {}) },
          detected_conditions: detectedConditions,
          session_id: sessionId,
          organization_id: resolvedOrgId || undefined,
          franchise_id: resolvedFranchiseId || undefined,
          product_recommendation_source: productRecommendationSource,
        });

        const body = {
          session_id: sessionId,
          final_skin_score: skinScore.final_skin_score || skinScore.score || 0,
          skin_grade: skinScore.grade || 'Fair',
          condition_scores: conditionScores,
          detected_conditions: detectedConditions,
          detections,
          skin_label_scores: skinScore.skin_label_scores || {},
          attribute_scores: skinScore.attribute_scores || {},
          app_session_token: token,
          product_recommendation_source: productRecommendationSource,
          ...(resolvedOrgId ? { organization_id: resolvedOrgId } : {}),
          ...(resolvedFranchiseId ? { franchise_id: resolvedFranchiseId } : {}),
          ...(genderForApi ? { gender: genderForApi } : {}),
          ...(userSkinType != null ? { user_skin_type: userSkinType } : {}),
          ...(userAge != null ? { user_age: userAge } : {}),
          ...(productRecommendationSource === 'products_page' && (productsPageUrls.length || productsPageUrl)
            ? {
                products_page_url: productsPageUrls[0] || productsPageUrl,
                products_page_urls: productsPageUrls.length ? productsPageUrls : [productsPageUrl],
              }
            : {}),
        };
        const response = await fetchWithTimeout(`${API_BASE_URL}${SALON_API_PREFIX}/recommend-products`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(body),
          timeoutMs: 30000,
        });

        if (!response.ok) {
          throw new Error('Failed to fetch recommendations');
        }

        const data = await response.json();
        console.log('[ProductRecommendations] recommend-products response:', {
          success: data.success,
          morning_count: (data.recommended_products?.morning || []).length,
          evening_count: (data.recommended_products?.evening || []).length,
          total_products_recommended: data.total_products_recommended
        });
        if (data.success) {
          setRecommendations(data);
          persistRecommendations(data);
        } else {
          throw new Error(data.error || 'Failed to get recommendations');
        }
      } catch (err) {
        setError(err.message || 'Failed to load product recommendations');
      } finally {
        setIsLoading(false);
      }
    };

    fetchRecommendations();
  }, [skinResultsFromState, userDataFromState, API_BASE_URL, sessionId, token, organizationId, recommendations, persistRecommendations, productSuggestionEnabled, isConfigLoaded, productRecommendationSource, productsPageUrl, productsPageUrls]);

  const handleCloseUspPopup = () => {
    setUspPopup({ open: false, text: '', heading: '', position: { top: 0, left: 0 } });
  };

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (uspPopup.open && !e.target.closest('.product-usp-popup') && !e.target.closest('.product-usp')) {
        handleCloseUspPopup();
      }
    };

    if (uspPopup.open) {
      document.addEventListener('click', handleClickOutside);
      return () => {
        document.removeEventListener('click', handleClickOutside);
      };
    }
  }, [uspPopup.open]);

  const handleBack = () => {
    const baseState = {
      capturedImage: capturedImageFromState,
      userData: userDataFromState,
      sessionId: sessionId,
      skinResults: skinResultsFromState,
      routineData: routineDataFromState,
      aqiData: aqiDataFromState,
      cartItems,
      treatmentCartItems,
      ...(token && { token }),
    };

    navigate(pathWithSessionId('/treatment-recommendations', sessionId), { state: baseState });
  };

  const handleHome = () => {
    goSalonHubForNewUser(navigate, {
      sessionId,
      token: token || null,
    });
  };

  return (
    <div className="product-recommendations-page-shell page-bg">
      <div className="product-recommendations-page-nav">
        <button
          type="button"
          className="product-recommendations-page-back-button"
          onClick={handleBack}
          aria-label="Go back"
        >
          ←
        </button>
        <div className="product-recommendations-page-title-wrapper">
          <SalonHeaderActions homeButtonClassName="product-recommendations-page-home-button" onHome={handleHome} />
          {getTotalCombinedCartItems() > 0 && (
            <button
              type="button"
              className="product-recommendations-page-checkout-button-icon"
              onClick={handleCheckout}
              title={`Checkout (${getTotalCombinedCartItems()} items)`}
              aria-label={`Checkout with ${getTotalCombinedCartItems()} items`}
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="9" cy="21" r="1"></circle>
                <circle cx="20" cy="21" r="1"></circle>
                <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"></path>
              </svg>
              {getTotalCombinedCartItems() > 0 && (
                <span className="product-recommendations-page-checkout-badge-icon">
                  {getTotalCombinedCartItems()}
                </span>
              )}
            </button>
          )}
        </div>
      </div>
      {!productSuggestionEnabled ? (
        <main className="product-recommendations-page-main">
          <div className="product-recommendations-page-container">
            <div className="product-recommendations-page-error">
              <p>Product recommendations are not enabled for this session.</p>
              <button type="button" className="skin-action-button" onClick={handleBack}>
                Back
              </button>
            </div>
          </div>
        </main>
      ) : (
        <main className="product-recommendations-page-main">
          <div className="product-recommendations-page-container">
            <section className="product-recommendations-page-layout">

            {isLoading ? (
              <div className="product-recommendations-loading">
                <div className="loading-spinner"></div>
                <p>Loading personalized recommendations...</p>
              </div>
            ) : error ? (
              <div className="product-recommendations-error">
                <p>{error}</p>
                <button onClick={() => window.location.reload()}>Retry</button>
              </div>
            ) : recommendations ? (
              <>
                {(() => {
                  const morningList = recommendations.recommended_products.morning || [];
                  const eveningList = recommendations.recommended_products.evening || [];
                  return (
                <div className="product-recommendations-content">
                  <div className="product-tabs">
                    <button
                      type="button"
                      className={`product-tab ${activeTab === 'morning' ? 'active' : ''}`}
                      onClick={() => setActiveTab('morning')}
                    >
                      <img
                        src={`${process.env.PUBLIC_URL}/icon/mor.png`}
                        alt="Morning"
                        className="product-tab-icon"
                      />
                      Morning ({morningList.length})
                    </button>
                    <button
                      type="button"
                      className={`product-tab ${activeTab === 'evening' ? 'active' : ''}`}
                      onClick={() => setActiveTab('evening')}
                    >
                      <img
                        src={`${process.env.PUBLIC_URL}/icon/eve.png`}
                        alt="Evening"
                        className="product-tab-icon"
                      />
                      Evening ({eveningList.length})
                    </button>
                  </div>

                  <div className="product-tab-panels">
                    {activeTab === 'morning' && (
                      <div className={`product-tab-panel ${morningList.length > 0 ? 'active' : ''}`}>
                        {morningList.length > 0 ? (
                          <div className="product-grid">
                            {morningList.map((product, idx) => {
                              const productId = product.product_id || product.id || `morning_${product.product_name}_${idx}`;
                              const cartQuantity = cartItems[productId]?.quantity || 0;
                              const showProductPrice = hasProductNetPrice(product);
                              const showProductQuantity = hasProductAmount(product);
                              const qtyForPrice = showProductQuantity ? Math.max(1, cartQuantity || 0) : 1;
                              const discountBadge = showProductPrice ? formatDiscountBadge(product) : null;
                              const productHref = resolveProductHref(product);
                              const showProductLinkCta = productLinkEnabled && isValidProductUrl(productHref);
                              return (
                                <div key={productId} className="product-card">
                                  {discountBadge ? (
                                    <div className="product-discount-badge" aria-label={`Discount ${discountBadge}`}>
                                      {discountBadge}
                                    </div>
                                  ) : null}
                                  <div className="product-card-image">
                                    {product.image_url ? (
                                      <img
                                        src={resolveProductImageUrl(product.image_url)}
                                        alt={product.product_name}
                                        onError={(e) => {
                                          e.target.style.display = 'none';
                                        }}
                                      />
                                    ) : null}
                                  </div>
                                  <div className="product-info">
                                    <div className="product-name-header">
                                      {showProductLinkCta ? (
                                        <h3 className="product-name product-name-link" onClick={(e) => handleProductLinkClick(product, productHref, e)} role="link" tabIndex={0} onKeyDown={(e) => { if (e.key === 'Enter') handleProductLinkClick(product, productHref, e); }}>
                                          {product.product_name}
                                        </h3>
                                      ) : (
                                        <h3 className="product-name">{product.product_name}</h3>
                                      )}
                                    </div>
                                    {(() => {
                                      const productTypeDisplay = product.product_type || product.productType || (Array.isArray(product.product_types) && product.product_types?.[0]) || product.product_types;
                                      return productTypeDisplay ? (
                                        <div className="product-type-heading">Type: {productTypeDisplay}</div>
                                      ) : null;
                                    })()}
                                    {product.major_usp && (() => {
                                      const majorUspPlain = stripHtml(product.major_usp);
                                      return (
                                        <div className="product-major-usp-wrapper">
                                          <span className="product-card-usp-heading">Key benefit</span>
                                          <span className="product-major-usp-text">{majorUspPlain}</span>
                                        </div>
                                      );
                                    })()}
                                    {product.concerns_treated && product.concerns_treated.length > 0 && (
                                      <div className="product-info-section">
                                        <div className="product-info-label">
                                          <img
                                            src={`${process.env.PUBLIC_URL}/icon/treat.png`}
                                            alt="Treats"
                                            className="product-info-icon"
                                          />
                                          <span>Treats</span>
                                        </div>
                                        <div className="product-tags-container">
                                          {product.concerns_treated.map((concern, i) => (
                                            <span key={i} className="product-tag">{concern}</span>
                                          ))}
                                        </div>
                                      </div>
                                    )}

                                    {product.skin_goals_supported && product.skin_goals_supported.length > 0 && (
                                      <div className="product-info-section">
                                        <div className="product-info-label">
                                          <img
                                            src={`${process.env.PUBLIC_URL}/icon/goal.png`}
                                            alt="Goals"
                                            className="product-info-icon"
                                          />
                                          <span>Goals</span>
                                        </div>
                                        <div className="product-tags-container">
                                          {product.skin_goals_supported.map((goal, i) => (
                                            <span key={i} className="product-tag">{goal}</span>
                                          ))}
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                  <div className="product-card-footer">
                                    {showProductPrice ? (
                                      <div className="product-price-quantity">
                                        {(() => {
                                          const unitNet = toNumberOrNull(product.amount) ?? 0;
                                          const unitSale = toNumberOrNull(product.sale_price) ?? unitNet;
                                          const totalNet = unitNet * qtyForPrice;
                                          const totalSale = unitSale * qtyForPrice;
                                          if (hasProductDiscount(product) && totalSale > 0 && totalSale < totalNet) {
                                            return (
                                              <>
                                                <span className="product-price product-net-price-slash">{`₹${totalNet.toFixed(0)}`}</span>
                                                <span className="product-price product-sale-price">{`₹${totalSale.toFixed(0)}`}</span>
                                              </>
                                            );
                                          }
                                          return (
                                            <span className="product-price">{`₹${totalNet.toFixed(0)}`}</span>
                                          );
                                        })()}
                                      </div>
                                    ) : null}
                                    {addToCartEnabled && showProductQuantity ? (
                                      <ProductCartCounter
                                        key={`counter-${productId}`}
                                        product={product}
                                        onAddToCart={(product, quantity) => handleAddToCart(product, quantity, productId)}
                                        disabled={false}
                                        initialQuantity={cartQuantity}
                                      />
                                    ) : addToCartEnabled ? (
                                      <button
                                        type="button"
                                        className={`skin-action-button add-to-cart-button ${cartQuantity > 0 ? 'request-selected' : ''}`}
                                        onClick={() => handleAddToCart(product, cartQuantity > 0 ? 0 : 1, productId)}
                                      >
                                        {cartQuantity > 0 ? 'Selected for Stock' : 'Add to Stock Request'}
                                      </button>
                                    ) : null}
                                    {showProductLinkCta ? (
                                      <button
                                        type="button"
                                        className="skin-action-button product-link-button"
                                        onClick={(e) => handleProductLinkClick(product, productHref, e)}
                                      >
                                        View product
                                      </button>
                                    ) : null}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        ) : (
                          <div className="no-products-message">
                            <p>No morning products found.</p>
                          </div>
                        )}
                      </div>
                    )}

                    {activeTab === 'evening' && (
                      <div className={`product-tab-panel ${eveningList.length > 0 ? 'active' : ''}`}>
                        {eveningList.length > 0 ? (
                          <div className="product-grid">
                            {eveningList.map((product, idx) => {
                              const productId = product.product_id || product.id || `evening_${product.product_name}_${idx}`;
                              const cartQuantity = cartItems[productId]?.quantity || 0;
                              const showProductPrice = hasProductNetPrice(product);
                              const showProductQuantity = hasProductAmount(product);
                              const qtyForPrice = showProductQuantity ? Math.max(1, cartQuantity || 0) : 1;
                              const discountBadge = showProductPrice ? formatDiscountBadge(product) : null;
                              const productHref = resolveProductHref(product);
                              const showProductLinkCta = productLinkEnabled && isValidProductUrl(productHref);
                              return (
                                <div key={productId} className="product-card">
                                  {discountBadge ? (
                                    <div className="product-discount-badge" aria-label={`Discount ${discountBadge}`}>
                                      {discountBadge}
                                    </div>
                                  ) : null}
                                  <div className="product-card-image">
                                    {product.image_url ? (
                                      <img
                                        src={resolveProductImageUrl(product.image_url)}
                                        alt={product.product_name}
                                        onError={(e) => {
                                          e.target.style.display = 'none';
                                        }}
                                      />
                                    ) : null}
                                  </div>
                                  <div className="product-info">
                                    <div className="product-name-header">
                                      {showProductLinkCta ? (
                                        <h3 className="product-name product-name-link" onClick={(e) => handleProductLinkClick(product, productHref, e)} role="link" tabIndex={0} onKeyDown={(e) => { if (e.key === 'Enter') handleProductLinkClick(product, productHref, e); }}>
                                          {product.product_name}
                                        </h3>
                                      ) : (
                                        <h3 className="product-name">{product.product_name}</h3>
                                      )}
                                    </div>
                                    {(() => {
                                      const productTypeDisplay = product.product_type || product.productType || (Array.isArray(product.product_types) && product.product_types?.[0]) || product.product_types;
                                      return productTypeDisplay ? (
                                        <div className="product-type-heading">Type: {productTypeDisplay}</div>
                                      ) : null;
                                    })()}
                                    {product.major_usp && (() => {
                                      const majorUspPlain = stripHtml(product.major_usp);
                                      return (
                                        <div className="product-major-usp-wrapper">
                                          <span className="product-card-usp-heading">Key benefit</span>
                                          <span className="product-major-usp-text">{majorUspPlain}</span>
                                        </div>
                                      );
                                    })()}
                                    {product.concerns_treated && product.concerns_treated.length > 0 && (
                                      <div className="product-info-section">
                                        <div className="product-info-label">
                                          <img
                                            src={`${process.env.PUBLIC_URL}/icon/treat.png`}
                                            alt="Treats"
                                            className="product-info-icon"
                                          />
                                          <span>Treats</span>
                                        </div>
                                        <div className="product-tags-container">
                                          {product.concerns_treated.map((concern, i) => (
                                            <span key={i} className="product-tag">{concern}</span>
                                          ))}
                                        </div>
                                      </div>
                                    )}

                                    {product.skin_goals_supported && product.skin_goals_supported.length > 0 && (
                                      <div className="product-info-section">
                                        <div className="product-info-label">
                                          <img
                                            src={`${process.env.PUBLIC_URL}/icon/goal.png`}
                                            alt="Goals"
                                            className="product-info-icon"
                                          />
                                          <span>Goals</span>
                                        </div>
                                        <div className="product-tags-container">
                                          {product.skin_goals_supported.map((goal, i) => (
                                            <span key={i} className="product-tag">{goal}</span>
                                          ))}
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                  <div className="product-card-footer">
                                    {showProductPrice ? (
                                      <div className="product-price-quantity">
                                        {(() => {
                                          const unitNet = toNumberOrNull(product.amount) ?? 0;
                                          const unitSale = toNumberOrNull(product.sale_price) ?? unitNet;
                                          const totalNet = unitNet * qtyForPrice;
                                          const totalSale = unitSale * qtyForPrice;
                                          if (hasProductDiscount(product) && totalSale > 0 && totalSale < totalNet) {
                                            return (
                                              <>
                                                <span className="product-price product-net-price-slash">{`₹${totalNet.toFixed(0)}`}</span>
                                                <span className="product-price product-sale-price">{`₹${totalSale.toFixed(0)}`}</span>
                                              </>
                                            );
                                          }
                                          return (
                                            <span className="product-price">{`₹${totalNet.toFixed(0)}`}</span>
                                          );
                                        })()}
                                      </div>
                                    ) : null}
                                    {addToCartEnabled && showProductQuantity ? (
                                      <ProductCartCounter
                                        key={`counter-${productId}`}
                                        product={product}
                                        onAddToCart={(product, quantity) => handleAddToCart(product, quantity, productId)}
                                        disabled={false}
                                        initialQuantity={cartQuantity}
                                      />
                                    ) : addToCartEnabled ? (
                                      <button
                                        type="button"
                                        className={`skin-action-button add-to-cart-button ${cartQuantity > 0 ? 'request-selected' : ''}`}
                                        onClick={() => handleAddToCart(product, cartQuantity > 0 ? 0 : 1, productId)}
                                      >
                                        {cartQuantity > 0 ? 'Selected for Stock' : 'Add to Stock Request'}
                                      </button>
                                    ) : null}
                                    {showProductLinkCta ? (
                                      <button
                                        type="button"
                                        className="skin-action-button product-link-button"
                                        onClick={(e) => handleProductLinkClick(product, productHref, e)}
                                      >
                                        View product
                                      </button>
                                    ) : null}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        ) : (
                          <div className="no-products-message">
                            <p>No evening products found.</p>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
                  );
                })()}
              </>
            ) : null}
          </section>
        </div>
      </main>
      )}

      {/* USP Popup */}
      {uspPopup.open && (
        <div
          className="product-usp-popup-overlay"
          onClick={handleCloseUspPopup}
        >
          <div
            className="product-usp-popup"
            style={{
              top: `${uspPopup.position.top}px`,
              left: `${uspPopup.position.left}px`,
              width: `${Math.max(uspPopup.position.width, 300)}px`,
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="product-usp-popup-content">
              <button
                className="product-usp-popup-close"
                onClick={handleCloseUspPopup}
                aria-label="Close"
              >
                ×
              </button>
              {uspPopup.heading && <h3 className="product-usp-popup-heading">{uspPopup.heading}</h3>}
              {(() => {
                const raw = String(uspPopup.text);
                const isHtml = /<[a-z][\s\S]*>/i.test(raw);
                const html = isHtml ? sanitizeHtml(raw) : sanitizeHtml(raw.replace(/\r\n/g, '\n').replace(/\r/g, '\n').replace(/\n/g, '<br />'));
                return <div className="product-usp-popup-text" dangerouslySetInnerHTML={{ __html: html }} />;
              })()}
            </div>
          </div>
        </div>
      )}


      {/* Checkout Modal (combined treatments + products) */}
      <CombinedCheckoutModal
        isOpen={isCheckoutModalOpen}
        onClose={handleCloseCheckoutModal}
        productCartItemsMap={cartItems}
        treatmentCartItemsMap={treatmentCartItems}
        onProductCartUpdate={handleCartUpdate}
        onTreatmentCartUpdate={handleTreatmentCartUpdate}
        sessionId={sessionId}
        apiBaseUrl={API_BASE_URL}
        token={token}
      />
    </div>
  );
};

export default ProductRecommendationsPage;

